import { useCallback, useEffect, useRef, useState } from 'react'
import { explain } from '@/shared/api/problem'
import {
  getIncident, getTimeline, listEvidence, listActions, loadVocabulary,
  type Incident, type TimelineEntry, type Evidence, type FollowUpAction,
  type IncidentVocabulary,
} from './api'

/**
 * One incident and everything hanging off it, reloaded together.
 *
 * ## Why all four, always
 *
 * Escalating writes a timeline row. Resolving changes the incident AND the timeline.
 * Withdrawing evidence writes a timeline row and changes the evidence list. Reloading
 * only the thing the caller thinks changed leaves the drawer showing a stale timeline
 * beside a fresh header, and **an action that visibly changes nothing reads as a
 * failure** — the user clicks it again.
 *
 * ## Why the id is in the dependency list
 *
 * It is null on first render while the drawer is closed. A `[]` dependency array would
 * bind the loader to that null forever, and the drawer would open permanently empty with
 * nothing saying why. Four station-mates pages shipped like this.
 */
export function useIncident(id: string | null) {
  const [incident, setIncident] = useState<Incident | null>(null)
  const [timeline, setTimeline] = useState<TimelineEntry[]>([])
  const [evidence, setEvidence] = useState<Evidence[]>([])
  const [actions, setActions] = useState<FollowUpAction[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // A late response from a previous id must never overwrite the current one's, and
  // nothing may be written after unmount.
  const seq = useRef(0)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const reload = useCallback(async () => {
    if (!id) {
      setIncident(null); setTimeline([]); setEvidence([]); setActions([])
      return
    }
    const mine = ++seq.current
    setLoading(true)
    setError(null)
    try {
      const [i, t, e, a] = await Promise.all([
        getIncident(id), getTimeline(id), listEvidence(id), listActions(id),
      ])
      if (!alive.current || seq.current !== mine) return
      setIncident(i); setTimeline(t); setEvidence(e); setActions(a)
    } catch (err) {
      if (!alive.current || seq.current !== mine) return
      setError(explain(err))
    } finally {
      if (alive.current && seq.current === mine) setLoading(false)
    }
  }, [id])

  useEffect(() => { void reload() }, [reload])

  return { incident, timeline, evidence, actions, error, loading, reload }
}

/**
 * The served vocabulary, loaded once per mount.
 *
 * Eleven small GETs fired in parallel. They are cached below for the life of the tab
 * because these are code lists that change on deploy, not on data — refetching them on
 * every drawer open would be eleven round trips to render a `<select>`.
 */
let cached: IncidentVocabulary | null = null
let inFlight: Promise<IncidentVocabulary> | null = null

export function useVocabulary() {
  const [vocab, setVocab] = useState<IncidentVocabulary | null>(cached)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (cached) return
    let alive = true
    inFlight ??= loadVocabulary()
    inFlight
      .then((v) => {
        cached = v
        if (alive) setVocab(v)
      })
      .catch((err) => {
        // Let a later mount try again — a vocabulary that failed once because the tab
        // was offline must not leave every `<select>` in the app empty until reload.
        inFlight = null
        if (alive) setError(explain(err))
      })
    return () => { alive = false }
  }, [])

  return { vocab, error }
}
